import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Phase 15 — CompletionToggleModal. Renders at the final PO stage for the
 * 7 form types (material, service, transportation, fnb, pocket_money,
 * advance_requisition, advance_settlement). Fires the `set_completion_status`
 * action with either "completed" or "in_progress".
 */
export default function CompletionToggleModal({ open, onClose, requisition, onDone }) {
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!status) { toast.error("Select Completed or In Progress"); return; }
    setBusy(true);
    try {
      const r = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        { action: "set_completion_status",
          completion_status: status,
          remarks: remarks.trim() },
      );
      toast.success(status === "completed"
        ? "Marked as Completed"
        : "Marked as In Progress");
      onDone && onDone(r.data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
         data-testid="completion-toggle-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-4">
        <div className="px-5 py-4 border-b border-[#e2d7c3] bg-[#7a1f2b] text-white rounded-t-lg">
          <h3 className="font-serif text-lg font-semibold">Update completion status · {requisition.ref_code}</h3>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className={`flex items-center gap-2 border rounded p-3 cursor-pointer ${status === "completed" ? "border-[#2d6b6b] bg-[#e6f2f0]" : "border-[#e2d7c3]"}`}>
              <input type="radio" name="cts" value="completed"
                     checked={status === "completed"}
                     onChange={() => setStatus("completed")}
                     data-testid="cts-completed" />
              <CheckCircle2 size={16} className="text-[#2d6b6b]" />
              <span className="text-sm font-medium">Completed</span>
            </label>
            <label className={`flex items-center gap-2 border rounded p-3 cursor-pointer ${status === "in_progress" ? "border-[#b48a45] bg-[#fbf3e4]" : "border-[#e2d7c3]"}`}>
              <input type="radio" name="cts" value="in_progress"
                     checked={status === "in_progress"}
                     onChange={() => setStatus("in_progress")}
                     data-testid="cts-in-progress" />
              <Clock size={16} className="text-[#b48a45]" />
              <span className="text-sm font-medium">In Progress</span>
            </label>
          </div>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold">Remarks (optional)</div>
            <textarea className="input-vgws mt-1 min-h-[80px]"
                      value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      data-testid="cts-remarks" />
          </label>
        </div>
        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={busy || !status} className="btn-vgws"
                  data-testid="cts-submit">
            {busy ? "Saving…" : "Save status"}
          </button>
        </div>
      </div>
    </div>
  );
}
